import { useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { loadAdyenScript } from '../../../lib/loadScript';
import { encryptCard } from '../../../lib/encryptCard';
import { COUNTRIES } from '../../../constants';
import {
  PAYMENT_METHODS_QUERY,
  PaymentMethodsQuery,
  BillingAddress
} from '../usePaymentMethodsQuery';
import {
  CREATE_PAYMENT_METHOD_MUTATION,
  CreatePaymentMethodMutation,
  CreatePaymentMethodMutationVariables
} from './createPaymentMethodMutation.rest';

type CreateArgs = {
  cardholderName: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  billingAddress: BillingAddress;
};

export const useCreatePaymentMethodMutation = () => {
  const [mutate, { loading, error }] = useMutation<
    CreatePaymentMethodMutation,
    CreatePaymentMethodMutationVariables
  >(CREATE_PAYMENT_METHOD_MUTATION, {
    update: (cache, { data }) => {
      if (!data) return;

      const cachedData = cache.readQuery<PaymentMethodsQuery>({ query: PAYMENT_METHODS_QUERY });

      if (!cachedData) return;

      cache.writeQuery<PaymentMethodsQuery>({
        query: PAYMENT_METHODS_QUERY,
        data: {
          paymentMethods: {
            __typename: 'RestPaymentMethods',
            data: [data.createPaymentMethod.data, ...cachedData.paymentMethods.data]
          }
        }
      });
    }
  });

  useEffect(() => {
    loadAdyenScript();
  }, []);

  const handleCreate = useCallback(
    ({
      cardholderName,
      cardNumber,
      expirationMonth,
      expirationYear,
      securityCode,
      billingAddress
    }: CreateArgs) => {
      const country = COUNTRIES.find(country => country.alpha2Code === billingAddress.countryCode);
      const encryptedCard = encryptCard({
        holderName: cardholderName,
        number: cardNumber,
        expiryMonth: expirationMonth,
        expiryYear: expirationYear,
        cvc: securityCode
      });

      if (!country) return Promise.reject('createPaymentMethodMutation: country not found');

      return mutate({
        variables: {
          input: {
            data: {
              type: 'payments',
              attributes: {
                encrypted_data: encryptedCard,
                billing_address: {
                  id: billingAddress.id,
                  country_name: country.name,
                  extended_address: billingAddress.extendedAddress,
                  locality: billingAddress.locality,
                  postal_code: billingAddress.postalCode,
                  region: billingAddress.region,
                  street_address: billingAddress.streetAddress
                }
              }
            }
          }
        }
      });
    },
    [mutate]
  );

  return [handleCreate, { loading, error }] as const;
};
